import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  TrendingUp, Users, Calendar, FileText, Trophy, GraduationCap,
  CircleAlert, Star, ArrowUpRight, Download,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const BRAND_COLORS = ["#5D3A1F", "#C28A2E", "#6B8E5A", "#D4A547", "#A07A45", "#A6741F", "#2A1810", "#8B5A2B"];

const KpiCard = ({ icon: Icon, label, value, accent = "primary", trend, testid }) => {
  const accentColors = {
    primary: { bg: "#5D3A1F", soft: "rgba(45, 90, 76, 0.08)" },
    accent: { bg: "#C28A2E", soft: "rgba(200, 106, 81, 0.10)" },
    success: { bg: "#6B8E5A", soft: "rgba(62, 123, 91, 0.10)" },
    warning: { bg: "#D4A547", soft: "rgba(217, 154, 67, 0.12)" },
    danger: { bg: "#A23E3E", soft: "rgba(184, 74, 74, 0.10)" },
  };
  const c = accentColors[accent];
  return (
    <div data-testid={testid} className="ccs-card ccs-card-hover p-5 lg:p-6">
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: c.soft }}>
          <Icon className="w-5 h-5" strokeWidth={1.8} style={{ color: c.bg }} />
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-xs font-medium text-[#6B8E5A]">
            <ArrowUpRight className="w-3 h-3" /> {trend}
          </div>
        )}
      </div>
      <div className="mt-5">
        <div className="font-display text-3xl font-semibold text-[#2A1810] tracking-tight">{value}</div>
        <div className="text-sm text-[#6B5944] mt-1">{label}</div>
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, testid }) => (
  <div data-testid={testid} className="ccs-card p-6">
    <div className="flex items-baseline justify-between mb-5">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">{subtitle}</div>
        <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">{title}</h3>
      </div>
    </div>
    {children}
  </div>
);

const fmt$ = (v) => `$${Number(v || 0).toLocaleString()}`;

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/charts")])
      .then(([s, c]) => { setStats(s.data); setCharts(c.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return <div className="text-[#6B5944] py-20 text-center font-display">Loading dashboard…</div>;
  }

  const monthLabel = (m) => {
    const [, mo] = m.split("-");
    return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(mo) - 1];
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header banner */}
      <div className="ccs-card ccs-grain p-7 lg:p-9 relative overflow-hidden">
        <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-20 hidden md:block pointer-events-none">
          <img
            src="https://customer-assets.emergentagent.com/job_ccs-consult/artifacts/hzn1wx4q_image.png"
            alt=""
            className="h-32 lg:h-36 w-auto"
          />
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">The CCS Approach · Business Snapshot</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-2">
            Your consulting practice at a glance
          </h1>
          <p className="text-[#6B5944] mt-2 max-w-2xl">
            A clear view of your pipeline, training engagements, and revenue — guided by the CCS framework:
            <span className="text-[#5D3A1F] font-semibold"> Communicate · Collaborate · Serve.</span>
          </p>
          <button
            onClick={async () => {
              try {
                const res = await api.get("/internal/platform-overview.pdf", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "the-ccs-approach-overview.pdf";
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => window.URL.revokeObjectURL(url), 30000);
              } catch (e) {
                console.error("PDF download failed", e);
              }
            }}
            data-testid="dashboard-overview-pdf-btn"
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#5D3A1F] hover:text-[#4A2E18] px-4 py-2 rounded-xl border border-[#EBE3D2] bg-white/70 hover:bg-white transition-all hover:-translate-y-[1px]"
          >
            <Download className="w-4 h-4 text-[#C28A2E]" />
            Platform Overview PDF (internal)
          </button>
        </div>
      </div>

      {/* KPIs */}
      <section>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          <KpiCard icon={Users} label="Total Leads" value={stats.total_leads} accent="primary" testid="kpi-total-leads" />
          <KpiCard icon={TrendingUp} label="Active Opportunities" value={stats.active_opportunities} accent="accent" testid="kpi-active-ops" />
          <KpiCard icon={Calendar} label="Discovery Meetings" value={stats.discovery_meetings} accent="success" testid="kpi-discovery" />
          <KpiCard icon={FileText} label="Proposals Sent" value={stats.proposals_sent} accent="warning" testid="kpi-proposals-sent" />
          <KpiCard icon={Trophy} label="Won Clients" value={stats.won_clients} accent="success" testid="kpi-won" />
          <KpiCard icon={GraduationCap} label="Workshops Scheduled" value={stats.workshops_scheduled} accent="primary" testid="kpi-ws-scheduled" />
          <KpiCard icon={GraduationCap} label="Workshops Completed" value={stats.workshops_completed} accent="accent" testid="kpi-ws-completed" />
          <KpiCard icon={Star} label="Avg Satisfaction" value={stats.client_satisfaction ? `${stats.client_satisfaction}/5` : "—"} accent="success" testid="kpi-satisfaction" />
        </div>
      </section>

      {/* Revenue strip */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="ccs-card p-7 lg:col-span-1" data-testid="revenue-pipeline-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Revenue Pipeline</div>
          <div className="font-display text-4xl font-semibold mt-3 text-[#2A1810]">{fmt$(stats.revenue_pipeline)}</div>
          <div className="text-sm text-[#6B5944] mt-2">Estimated value of opportunities in flight</div>
        </div>
        <div className="ccs-card p-7 lg:col-span-1" data-testid="revenue-won-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Revenue Won</div>
          <div className="font-display text-4xl font-semibold mt-3 text-[#5D3A1F]">{fmt$(stats.revenue_won)}</div>
          <div className="text-sm text-[#6B5944] mt-2">Closed-won engagements & accepted proposals</div>
        </div>
        <div className="ccs-card p-7 lg:col-span-1" data-testid="urgent-actions-card">
          <div className="flex items-center gap-2">
            <CircleAlert className="w-4 h-4 text-[#C28A2E]" strokeWidth={1.8} />
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Urgent Actions</div>
          </div>
          <div className="font-display text-4xl font-semibold mt-3 text-[#2A1810]">{stats.follow_ups_due}</div>
          <div className="text-sm text-[#6B5944] mt-2">Follow-ups overdue — see Notifications</div>
        </div>
      </section>

      {/* Charts */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Revenue by Month" subtitle="Last 6 months" testid="chart-revenue-month">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={charts?.revenue_by_month || []}>
              <CartesianGrid stroke="#EBE3D2" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} stroke="#6B5944" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6B5944" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} formatter={(v) => fmt$(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#5D3A1F" strokeWidth={2.5} dot={{ r: 4, fill: "#C28A2E" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads by Source" subtitle="Channel breakdown" testid="chart-leads-source">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={charts?.leads_by_source || []} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={85} innerRadius={50} paddingAngle={3}>
                {(charts?.leads_by_source || []).map((_, i) => (<Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />))}
              </Pie>
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Services Most Requested" subtitle="Top demand" testid="chart-services">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={charts?.services_requested || []} layout="vertical" margin={{ left: 0 }}>
              <CartesianGrid stroke="#EBE3D2" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" stroke="#6B5944" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="service" stroke="#6B5944" tick={{ fontSize: 10 }} width={130} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} />
              <Bar dataKey="count" fill="#C28A2E" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Pipeline by Stage" subtitle="Conversion view" testid="chart-stages">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={charts?.stages || []}>
              <CartesianGrid stroke="#EBE3D2" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="stage" stroke="#6B5944" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={70} interval={0} />
              <YAxis stroke="#6B5944" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} />
              <Bar dataKey="count" fill="#5D3A1F" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Training Engagement Trends" subtitle="Workshops by month" testid="chart-workshops">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={charts?.workshops_by_month || []}>
              <CartesianGrid stroke="#EBE3D2" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} stroke="#6B5944" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6B5944" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#6B8E5A" strokeWidth={2.5} dot={{ r: 4, fill: "#6B8E5A" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="CCS Philosophy" subtitle="Communicate · Collaborate · Serve" testid="card-ccs-philosophy">
          <div className="space-y-3 mt-2">
            {[
              { letter: "C", title: "Communicate", desc: "Active listening, professionalism, difficult conversations" },
              { letter: "C", title: "Collaborate", desc: "Teamwork, accountability, respect, conflict management" },
              { letter: "S", title: "Serve", desc: "Internal & external service excellence, relationships" },
            ].map((row) => (
              <div key={row.title} className="flex items-start gap-3 p-3 rounded-xl hover:bg-[#FDFBF5] transition">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center font-display font-bold text-white text-base shrink-0"
                  style={{ background: row.title === "Communicate" ? "#5D3A1F" : row.title === "Collaborate" ? "#C28A2E" : "#8B5A2B" }}>
                  {row.letter}
                </div>
                <div>
                  <div className="font-medium text-[#2A1810] text-sm">{row.title}</div>
                  <div className="text-xs text-[#6B5944] mt-0.5 leading-relaxed">{row.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </section>
    </div>
  );
};

export default DashboardPage;
